import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties, ComputedRef, Ref } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { Subscription, SubscriptionOrder } from '@entities/subscription'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { useOrderSync } from '@features/order-actions'
import { getOrderUrl } from '@shared/lib/routes'

import { useSubscriptionsStore } from './store'
import { useSubscriptionsApi } from '../api/list.hook'
import { canRetryPayment } from './subscription-rules'

interface UseSubscriptionOrderActionsOptions {
  order: ComputedRef<SubscriptionOrder>
  subscription: ComputedRef<Subscription>
  canManage: ComputedRef<boolean>
}

interface SubscriptionOrderActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  isRetryVisible: ComputedRef<boolean>
  toggle: () => void
  openOrder: () => Promise<void>
  syncOrder: () => Promise<void>
  openRetry: () => void
}

export const useSubscriptionOrderActions = ({
  order,
  subscription,
  canManage,
}: UseSubscriptionOrderActionsOptions): SubscriptionOrderActionsReturn => {
  const store = useSubscriptionsStore()

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const isRetryVisible = computed<boolean>(() => canManage.value && canRetryPayment(order.value, subscription.value))

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const openOrder = async (): Promise<void> => {
    isOpen.value = false
    await navigateTo(getOrderUrl(order.value.id))
  }

  const syncOrder = async (): Promise<void> => {
    isOpen.value = false

    const synced = await useOrderSync(order.value.id)

    if (synced) {
      await useSubscriptionsApi()
    }
  }

  const openRetry = (): void => {
    isOpen.value = false
    store.setRetryTarget({ subscriptionId: subscription.value.id, orderId: order.value.id })
  }

  onClickOutside(containerRef, () => {
    isOpen.value = false
  })

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    isRetryVisible,
    toggle,
    openOrder,
    syncOrder,
    openRetry,
  }
}
